import { useState } from 'react';

const ORG_TYPES = [
  'Select organization type',
  'Private enterprise',
  'Public sector',
  'Healthcare',
  'Education',
  'Non-profit',
  'Other',
];

export default function Enterprise() {
  const [form, setForm] = useState({
    name: '', email: '', org: '', orgType: '', knowledge: ''
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name.trim())       e.name     = 'Name is required.';
    if (!form.email.trim())      e.email    = 'Work email is required.';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Enter a valid email address.';
    if (!form.org.trim())        e.org      = 'Organization is required.';
    if (!form.orgType || form.orgType === ORG_TYPES[0])
                                 e.orgType  = 'Select an organization type.';
    if (!form.knowledge.trim())  e.knowledge = 'Please describe the knowledge domain.';
    return e;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    // Preview: no transmission, no storage, no logging
    setSubmitted(true);
  };

  const field = (key, label, type = 'text', placeholder = '') => (
    <div>
      <label
        htmlFor={`field-${key}`}
        className="font-heading font-semibold"
        style={{ fontSize: '12px', color: '#667085', textTransform: 'uppercase', letterSpacing: '0.07em', display: 'block', marginBottom: '6px' }}
      >
        {label}
      </label>
      <input
        id={`field-${key}`}
        type={type}
        value={form[key]}
        placeholder={placeholder}
        onChange={e => { setForm(f => ({ ...f, [key]: e.target.value })); setErrors(er => ({ ...er, [key]: '' })); }}
        style={{
          display: 'block',
          width: '100%',
          height: '46px',
          border: `1px solid ${errors[key] ? '#000' : '#d9dee6'}`,
          borderRadius: '3px',
          padding: '0 14px',
          fontFamily: 'Helvetica, Arial, sans-serif',
          fontSize: '16px',
          color: '#000',
          background: '#fff',
          outline: 'none',
          boxSizing: 'border-box',
        }}
        data-testid={`enterprise-field-${key}`}
      />
      {errors[key] && (
        <p style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', color: '#000', marginTop: '4px' }} data-testid={`enterprise-error-${key}`}>{errors[key]}</p>
      )}
    </div>
  );

  return (
    <div data-testid="enterprise-page">

      {/* Hero */}
      <section className="pub-section pub-bg-black">
        <div className="pub-container">
          <p className="pub-eyebrow mb-5">Enterprise</p>
          <h1 className="pub-heading-hero" style={{ maxWidth: '680px' }}>
            Bring governed knowledge to your organization.
          </h1>
          <p className="pub-body-lg mt-6" style={{ color: '#98a2b3', maxWidth: '560px' }}>
            Tell us where authoritative knowledge changes, conflicts, or gets lost. We will help determine whether a bounded KYRDROS pilot fits your organization.
          </p>
        </div>
      </section>

      {/* Form section */}
      <section className="pub-section pub-bg-white">
        <div className="pub-container">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-16 items-start">
            {/* Form */}
            <div style={{ maxWidth: '560px' }}>
              {submitted ? (
                <div
                  style={{ border: '1px solid #d9dee6', borderRadius: '4px', padding: '40px', background: '#f9fafb' }}
                  data-testid="enterprise-confirmation"
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#03f37c', flexShrink: 0 }} />
                    <p className="font-heading font-bold text-black" style={{ fontSize: '18px' }}>Request received.</p>
                  </div>
                  <p className="pub-body" style={{ color: '#667085', marginBottom: '12px' }}>
                    This is a preview confirmation. In production, your inquiry would be routed to the Durahuman enterprise team.
                  </p>
                  <p style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', color: '#98a2b3', background: '#f0f2f5', padding: '10px 14px', borderRadius: '3px' }}>
                    PREVIEW NOTE — No data was transmitted, stored, or logged. Enterprise contact routing is an open production gate.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate className="space-y-6" data-testid="enterprise-form">
                  {field('name',  'Name',             'text',  'Your full name')}
                  {field('email', 'Work email',        'email', 'you@organization.com')}
                  {field('org',   'Organization',      'text',  'Organization name')}

                  {/* Org type select */}
                  <div>
                    <label
                      htmlFor="field-orgType"
                      className="font-heading font-semibold"
                      style={{ fontSize: '12px', color: '#667085', textTransform: 'uppercase', letterSpacing: '0.07em', display: 'block', marginBottom: '6px' }}
                    >
                      Organization type
                    </label>
                    <select
                      id="field-orgType"
                      value={form.orgType}
                      onChange={e => { setForm(f => ({ ...f, orgType: e.target.value })); setErrors(er => ({ ...er, orgType: '' })); }}
                      style={{
                        display: 'block',
                        width: '100%',
                        height: '46px',
                        border: `1px solid ${errors.orgType ? '#000' : '#d9dee6'}`,
                        borderRadius: '3px',
                        padding: '0 14px',
                        fontFamily: 'Helvetica, Arial, sans-serif',
                        fontSize: '16px',
                        color: form.orgType && form.orgType !== ORG_TYPES[0] ? '#000' : '#98a2b3',
                        background: '#fff',
                        outline: 'none',
                        boxSizing: 'border-box',
                        appearance: 'none',
                        cursor: 'pointer',
                      }}
                      data-testid="enterprise-field-orgType"
                    >
                      {ORG_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    {errors.orgType && (
                      <p style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', color: '#000', marginTop: '4px' }}>{errors.orgType}</p>
                    )}
                  </div>

                  {/* Knowledge textarea */}
                  <div>
                    <label
                      htmlFor="field-knowledge"
                      className="font-heading font-semibold"
                      style={{ fontSize: '12px', color: '#667085', textTransform: 'uppercase', letterSpacing: '0.07em', display: 'block', marginBottom: '6px' }}
                    >
                      What knowledge needs to be governed?
                    </label>
                    <textarea
                      id="field-knowledge"
                      value={form.knowledge}
                      onChange={e => { setForm(f => ({ ...f, knowledge: e.target.value })); setErrors(er => ({ ...er, knowledge: '' })); }}
                      rows={4}
                      placeholder="Describe the policies, procedures, or guidance that is difficult to keep authoritative and consistent…"
                      style={{
                        display: 'block',
                        width: '100%',
                        border: `1px solid ${errors.knowledge ? '#000' : '#d9dee6'}`,
                        borderRadius: '3px',
                        padding: '12px 14px',
                        fontFamily: 'Helvetica, Arial, sans-serif',
                        fontSize: '16px',
                        color: '#000',
                        background: '#fff',
                        outline: 'none',
                        resize: 'vertical',
                        boxSizing: 'border-box',
                        lineHeight: 1.55,
                      }}
                      data-testid="enterprise-field-knowledge"
                    />
                    {errors.knowledge && (
                      <p style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', color: '#000', marginTop: '4px' }}>{errors.knowledge}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="pub-btn-primary pub-btn-primary-lg"
                    style={{ width: '100%', justifyContent: 'center' }}
                    data-testid="enterprise-submit-btn"
                  >
                    Send inquiry
                  </button>

                  <p style={{ fontFamily: 'Helvetica, Arial, sans-serif', fontSize: '12px', color: '#98a2b3', lineHeight: 1.5 }}>
                    This preview does not transmit or store submitted information. Enterprise contact routing is an open production gate.
                  </p>
                </form>
              )}
            </div>

            {/* Right: why a pilot */}
            <div>
              <h2 className="font-heading font-bold text-black mb-6" style={{ fontSize: '22px' }}>
                What a bounded pilot looks like
              </h2>
              <div className="space-y-5">
                {[
                  { title: 'Defined scope', body: 'A single knowledge domain — one policy set, one training library, one research corpus. Not the whole organization.' },
                  { title: 'Existing sources',  body: 'We work with the material you already have. No ingestion infrastructure required for the pilot phase.' },
                  { title: 'Named authorities', body: 'A pilot requires identifying the humans who have decision authority in the defined scope. KYRDROS routes to people, not queues.' },
                  { title: 'Governance record', body: 'At the end of the pilot, you have an audit trail of what was reviewed, what was decided, and who made each call.' },
                ].map(it => (
                  <div key={it.title} style={{ borderLeft: '2px solid #d9dee6', paddingLeft: '16px' }}>
                    <p className="font-heading font-semibold text-black mb-1" style={{ fontSize: '14px' }}>{it.title}</p>
                    <p className="pub-body" style={{ color: '#667085', fontSize: '14px', lineHeight: 1.6 }}>{it.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
